import React, { useEffect, useState } from 'react';
// Helpers
import isNil from 'lodash/isNil';
import cn from 'classnames';
import { filterBy } from '@progress/kendo-data-query';
// Components
// eslint-disable-next-line no-unused-vars
import { ComboBox, ComboBoxProps, DropDownList, DropDownListProps } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import AsyncSelect from 'shared/inputs/AsyncSelect';
import { Link } from 'react-router-dom';
import BaseIcon from 'shared/icons/BaseIcon';
import BaseAutoComplete from './BaseAutoComplete';
// Consts
import { ENTER_KEY_CODE, TAB_KEY_CODE } from 'shared/../constants/keyCodes';
import uniqBy from 'lodash/uniqBy';
import { emptyArray } from 'src/shared/helpers/utils';

const ReadOnlySelect = ({ value, textField, goesTo, ...props }) => {
  const className = cn('read-only', { empty: isNil(value) });
  const readOnlyValue = isNil(value) ? 'Not selected' : value[textField];
  return (
    <div className={className} {...props}>
      {goesTo && (
        <Link to={goesTo} target='_blank' rel="noreferrer" style={{ width: 'fit-content' }}>
          {readOnlyValue}
          <span className='k-icon k-i-hyperlink-open-sm' />
        </Link>
      )}
      {!goesTo && readOnlyValue}
    </div>
  );
};

/**
 * A base select component with various options and handlers.
 *
 * @typedef {React.PropsWithoutRef<ComboBoxProps>} TelerikComboBoxPropsProps
 * @typedef {React.PropsWithoutRef<DropDownListProps>} TelerikDropDownListPropsProps
 * @typedef {React.ComponentProps<BaseAutoComplete>} BaseAutoCompleteProps
 *
 * @typedef {Object} AdditionalSelectProps
 * @property {string} [dataTestId] Data test Id.
 * @property {string} [id] Unique identifier for the select.
 * @property {string} [dataItemKey='value'] "value" by default.
 * @property {string} [textField='description'] "description" by default.
 * @property {Function} [onQuickAdd] A callback when clicking Plus icon button. The button is added if the callback is set.
 * @property {boolean} [forceEnableQuickAdd] Always enable Plus icon button which calls onQuickAdd callback.
 * @property {Function} [onItemOpen] A callback when clicking Link icon button. The button is added if the callback is set.
 * @property {boolean} [isCombo={true}] Indicates whether the select expands Telerik ComboBox component. True by default.
 * @property {boolean} [primitive={false}] Indicates if the data is a flat array and a value is a key. False by default.
 * @property {boolean} [keyOnly={false}] Indicates if only keys are used as a value. False by default.
 * @property {React.ReactElement} [actionComponent] Custom action component after the select.
 * @property {boolean} [minimal={false}] Indicates whether the select uses a minimal styling (Expands BaseAutoComplete). False by default.
 * @property {boolean} [selectFirstItemOnFocus={false}] Select first item on focus. False by default.
 * @property {boolean} [autoFilterById={true}] Auto-filter by ID. True by default.
 * @property {string} [goesTo] A link path to open a new browser tab, added after the input.
 * @property {boolean} [hasWarning={false}] Indicates if the select has a warning message. False by default.
 * @property {string} [warningTitle] Warning message text.
 * @property {function} [onWarningClick] Callback when warning message is clicked.
 * @property {function} [asyncSearch] Callback when async select is used (Expands AsyncSelect).
 * @property {boolean} [closeOnClear=false] Indicates if dropdown closes on clear button click.
 * @property {function} [onWrapperClick] Callback when the wrapper is clicked.
 *
 * @param {TelerikComboBoxPropsProps & TelerikDropDownListPropsProps & BaseAutoCompleteProps & AdditionalSelectProps} props Component props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Base select component.
 */
const BaseSelect = ({
  value,
  onChange,
  dataItemKey = 'value',
  textField = 'description',
  data: passedData,
  readOnly,
  forceEnableQuickAdd,
  onQuickAdd,
  onItemOpen = null,
  disabled,
  isCombo = true,
  primitive = false,
  keyOnly = false,
  actionComponent,
  minimal = false,
  id,
  selectFirstItemOnFocus = false,
  // Filter
  filterable = true,
  autoFilterById = true,
  // Handlers
  onFocus,
  onBlur,
  suggest = true,
  dataTestId,
  goesTo,
  hasWarning = false,
  warningTitle,
  onWarningClick,
  asyncSearch,
  closeOnClear = false,
  onWrapperClick,
  ...props
}) => {
  const data = passedData || emptyArray;
  // Filtered data
  const [options, setOptions] = useState(data || []);
  const [filter, setFilter] = useState();

  const onFilterChange = (e) => {
    setFilter(e.filter);
  };

  // Value
  const innerValue = (typeof value === 'object' || primitive || isNil(value)
    ? (value ?? null)
    : options.find((item) => item[dataItemKey] == (value?.[dataItemKey] ?? value?.value ?? value?.id ?? value)) ?? null);

  // Support open on focus and close on blur
  const [opened, setOpened] = useState(false);

  const handleAutoFilterById = (opts) => {
    const dataWithStrId = opts?.map((d) => ({
      ...d,
      _dataItemKeyStr: d[dataItemKey]?.toString(),
    }));

    const labelFiltered = filterBy(dataWithStrId, filter);

    const idFilter = {
      field: '_dataItemKeyStr',
      operator: 'contains',
      ignoreCase: true,
      value: filter?.value,
    };

    const idFiltered = filterBy(dataWithStrId, idFilter);

    const cleanDt = [...labelFiltered, ...idFiltered]?.map((row) => {
      const { _dataItemKeyStr, ...rest } = row;
      return rest;
    });
    const newDt = uniqBy(cleanDt, dataItemKey);
    setOptions(newDt);
  };

  const handleFocus = (e) => {
    if (!disabled && !readOnly) {
      setOpened(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setOpened(false);
    if (filterable) {
      setFilter(null);
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    if (filterable && !minimal) {
      setFilter(null);
    }
    const { value: newValue, nativeEvent } = e;

    // Inner input loosing focus on tab causes cleaning value - prevent it
    if (nativeEvent?.type === 'focusout') {
      return;
    }

    if (onChange) {
      const result = (keyOnly && !isNil(newValue)) ? newValue[dataItemKey] : newValue;
      onChange({ ...e, value: result, target: e.target ? { ...e.target, value: result } : { value: result } });
    }

    // Close if mouse event
    if (nativeEvent?.pointerType === 'mouse') {
      setOpened(false);
    }

    // Close if clear button is clicked
    if (closeOnClear && !nativeEvent && !newValue) {
      handleBlur(e);
    }
  };

  // Handle Tab press to select a focused item on search and move next
  const handleKeyDown = (e) => {
    if ((e.keyCode === TAB_KEY_CODE || e.keyCode === ENTER_KEY_CODE) && opened) {
      e.stopPropagation();

      if (selectFirstItemOnFocus) {
        // Get the element focused and its value
        const focused = document.getElementsByClassName('k-list-item k-focus')?.[0];
        if (!focused) { // Skip if no focused element
          return;
        }
        if (focused.classList.contains('k-selected')) { // Skip if already selected
          setOpened(false);
          return;
        }
        const text = focused.getElementsByClassName('k-list-item-text')?.[0]?.innerText;
        if (!text) {
          return;
        }
        const selectedValue = options.find((o) => (primitive ? o === text : o[textField] === text));
        // Skip if already selected
        if (selectedValue && innerValue
          && (innerValue === selectedValue || (!primitive && innerValue[dataItemKey] === selectedValue[dataItemKey]))) {
          return;
        }
        handleChange({ ...e, target: { ...e.target, value: selectedValue }, value: selectedValue });
      }

      setOpened(false);
    }
  };

  const handleWrapperClick = (e) => {
    if (!disabled && !readOnly) {
      setOpened(true);
    }
    if (onWrapperClick) {
      onWrapperClick(e);
    }
  };

  useEffect(() => {
    const notEmptyItems = primitive
      ? data.filter((item) => !isNil(item)) : data.filter((item) => (!isNil(item[textField])));

    if (autoFilterById && filter) {
      handleAutoFilterById(notEmptyItems);
    } else if (filterable && filter) {
      setOptions(filterBy(notEmptyItems, filter));
    } else {
      setOptions(notEmptyItems);
    }
  }, [
    data,
    primitive,
    filterable,
    filter,
    autoFilterById,
  ]);

  const SelectInput = () => {
    if (minimal) {
      return (
        <span onKeyDown={handleKeyDown} onClick={handleWrapperClick} data-testid={dataTestId}>
          <BaseAutoComplete
            value={innerValue}
            dataItemKey={dataItemKey}
            textField={textField}
            data={options}
            readOnly={readOnly}
            forceEnableQuickAdd={forceEnableQuickAdd}
            onQuickAdd={onQuickAdd}
            disabled={disabled}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={handleFocus}
            {...props}
          />
        </span>
      );
    }

    if (typeof asyncSearch === 'function') {
      return (
        <span onKeyDown={handleKeyDown} onClick={handleWrapperClick} data-testid={dataTestId}>
          <AsyncSelect
            getData={(name) => asyncSearch(name)}
            placeholder={props.placeHolder ?? 'Type to search'}
            dataItemKey={dataItemKey ?? 'id'}
            textField={textField ?? 'name'}
            value={innerValue}
            readOnly={readOnly}
            disabled={disabled}
            onChange={onChange}
            multiSelect={props.multiSelect}
            reloadOnOpen
            fetchOnMount
            itemRender={props.itemRender}
            id={id}
            onBlur={onBlur}
            onFocus={handleFocus}
          />
        </span>
      );
    }

    if (isCombo) {
      return (
        <span onKeyDown={handleKeyDown} onClick={handleWrapperClick} data-testid={dataTestId}>
          <ComboBox
            value={innerValue}
            data={options}
            dataItemKey={primitive ? undefined : dataItemKey}
            textField={primitive ? undefined : textField}
            disabled={disabled}
            suggest={suggest}
            onChange={handleChange}
            opened={opened}
            onBlur={handleBlur}
            onFocus={handleFocus}
            filterable={filterable}
            onFilterChange={onFilterChange}
            {...props}
          />
        </span>
      );
    }

    return (
      <span onKeyDown={handleKeyDown} onClick={handleWrapperClick} data-testid={dataTestId}>
        <DropDownList
          value={innerValue}
          data={options}
          dataItemKey={dataItemKey}
          textField={textField}
          disabled={disabled}
          onChange={handleChange}
          opened={opened}
          onBlur={handleBlur}
          onFocus={handleFocus}
          filterable={filterable}
          onFilterChange={onFilterChange}
          {...props}
        />
      </span>
    );
  };

  const SelectComponent = (extra) => (
    <div className='input-items-container'>
      {
        SelectInput()
      }
      {goesTo && <Link to={goesTo} target='_blank' className='k-icon k-i-hyperlink-open' rel="noreferrer" />}
      {hasWarning && <BaseIcon name='warning' className='warning' title={warningTitle} onClick={onWarningClick} />}
      {extra}
    </div>
  );

  if (readOnly) {
    return <ReadOnlySelect value={innerValue} textField={textField} data-testid={dataTestId} goesTo={goesTo} />;
  }

  if (onQuickAdd && (isNil(value) || isNil(onItemOpen) || forceEnableQuickAdd)) {
    return SelectComponent(<Button
      icon='plus'
      type='button'
      fillMode='flat'
      themeColor='tertiary'
      disabled={disabled && !forceEnableQuickAdd}
      onClick={onQuickAdd}
      tabIndex={-1}
    />);
  }

  if (!forceEnableQuickAdd && !isNil(onItemOpen) && !isNil(value)) {
    return SelectComponent(<Button
      icon='hyperlink-open'
      type='button'
      fillMode='flat'
      themeColor='tertiary'
      onClick={onItemOpen}
      tabIndex={-1}
    />);
  }

  if (actionComponent) {
    return SelectComponent(actionComponent);
  }

  return SelectComponent();
};

export default BaseSelect;
